import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { useClient, useQuery } from "jsonapi-react";
import { VW_ROUTES, apiRoutes } from "../../../constants";
import { getKeyName } from "../../../helpers/utils";
import ReactTooltip from "react-tooltip";
import { isWidgetRefreshable } from "../../../helpers/helpers";
import Axios from "axios";
import { AppContext } from "../../../context/AppContext";
import { useToasts } from "react-toast-notifications";
import { GLOBAL_CONFIG } from "../../../config";
import ProgressBar from "../ProgressBar/ProgressBar";
import "./licenseQuotaWidget.scss";

// SVG arc total length for a semi-circle gauge (radius 72, 180 degrees)
const ARC_LENGTH = 226.2;

const fmt = (n) => (n != null ? n.toLocaleString() : "—");

const clampPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(n, 100));
};

const getState = (pct) => {
  if (pct >= 75) return "critical";
  if (pct >= 60) return "warning";
  return "normal";
};

const getSumOfActive = (obj) => {
  if (!obj || typeof obj !== "object") return 0;
  let sum = 0;
  for (const k in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, k) &&
      typeof obj[k] === "number" &&
      (k === "active" || k === "banned")
    ) {
      sum += obj[k];
    }
  }
  return sum;
};

/* ------------------------------------------------------------------ */
/*  SVG Gauge                                                          */
/* ------------------------------------------------------------------ */
const Gauge = ({ used, total, percent }) => {
  const state = getState(percent);
  const fill = (percent / 100) * ARC_LENGTH;

  return (
    <div className="gauge-section">
      {/* Hidden SVG gradients */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient id="lqw-gradient-normal" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2ac198" />
            <stop offset="100%" stopColor="#3890c3" />
          </linearGradient>
          <linearGradient id="lqw-gradient-warning" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ebb575" />
            <stop offset="100%" stopColor="#ec9039" />
          </linearGradient>
          <linearGradient id="lqw-gradient-critical" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#d6285f" />
            <stop offset="100%" stopColor="#e45072" />
          </linearGradient>
        </defs>
      </svg>

      <div className="gauge-container">
        <svg className="gauge-svg" viewBox="0 0 180 108">
          <path className="gauge-track" d="M 18,90 A 72,72 0 0,1 162,90" />
          <path
            className={`gauge-fill gauge-fill-${state}`}
            d="M 18,90 A 72,72 0 0,1 162,90"
            strokeDasharray={`${fill} ${ARC_LENGTH}`}
          />
        </svg>
        <div className="gauge-center">
          <div className={`gauge-value gauge-value-${state}`}>{fmt(used)}</div>
          <div className="gauge-label">of {fmt(total)} global</div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Linear usage bar                                                   */
/* ------------------------------------------------------------------ */
const UsageBar = ({ used, total, percent, pkgPercent }) => {
  const state = getState(percent);
  return (
    <div className="usage-row">
      <div className="usage-bar-track">
        <div
          className={`usage-bar-fill usage-bar-fill-${state}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
        {pkgPercent != null && (
          <div className="usage-bar-pkg" style={{ left: `${Math.min(pkgPercent, 100)}%` }} />
        )}
      </div>
      <div className="usage-info">
        <span className="usage-label">Profile usage :</span>
        <span className="usage-numbers">
          <strong>{fmt(used)}</strong> / {fmt(total)}
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Package info row                                                   */
/* ------------------------------------------------------------------ */
const PackageRow = ({ name, count }) => (
  <div className="pkg-info">
    <span className="pkg-label">
      This package (<strong>{name}</strong>)
    </span>
    <span className="pkg-count">
      {fmt(count)} <span>profiles</span>
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Category card (Employee / Visitor)                                 */
/* ------------------------------------------------------------------ */
const handleKeyDown = (callback) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    callback();
  }
};

const CategoryCard = ({ categoryKey, data, onNavigate }) => {
  const safeData = data && typeof data === "object" ? data : {};
  const total = getSumOfActive(safeData);
  const activeCount = safeData.active || 0;
  const archivedCount = safeData.archived || 0;
  const bannedCount = safeData.banned || 0;
  const barPercent = total > 0 ? (activeCount / total) * 100 : 0;

  const isEmployee = categoryKey === "employee";

  const miniBar = (value, color) => {
    const pct = total > 0 ? (value / total) * 100 : 0;
    return (
      <div className="status-bar-mini">
        <div
          className="status-bar-mini-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    );
  };

  const statusRow = (label, dotClass, value, color, status) => (
    <div
      className="status-row"
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(categoryKey, status)}
      onKeyDown={handleKeyDown(() => onNavigate(categoryKey, status))}
    >
      <div className="status-left">
        <span className={`status-dot ${dotClass}`} />
        <span className="status-name">{label}</span>
      </div>
      <div className="status-right">
        {miniBar(value, color)}
        <span className="status-count">{value}</span>
        <span className="arrow">&#8250;</span>
      </div>
    </div>
  );

  return (
    <div className="cat-card">
      <div className="cat-header">
        <div className="cat-icon-label">
          <div className={`cat-icon cat-icon-${categoryKey}`}>
            {isEmployee ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="#2ac198" strokeWidth="2">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="#3890c3" strokeWidth="2">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                />
              </svg>
            )}
          </div>
          <span className="cat-name">{getKeyName(categoryKey)}</span>
        </div>
        <span
          className="cat-total-badge"
          role="button"
          tabIndex={0}
          data-tip="Sum of Active and Banned profiles"
          onClick={() => onNavigate(categoryKey, "")}
          onKeyDown={handleKeyDown(() => onNavigate(categoryKey, ""))}
        >
          {total}
        </span>
      </div>

      <div className="cat-bar-track">
        <div className="cat-bar-fill cat-bar-fill-active" style={{ width: `${barPercent}%` }} />
      </div>

      <div className="status-rows">
        {statusRow("Active", "dot-active", activeCount, "var(--teal, #2ac198)", "active")}
        {GLOBAL_CONFIG.profile.archive &&
          statusRow("Archived", "dot-archived", archivedCount, "var(--teal-end, #3890c3)", "archived")}
        {statusRow("Banned", "dot-banned", bannedCount, "var(--red, #d6285f)", "banned")}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Skeleton loader                                                    */
/* ------------------------------------------------------------------ */
const Skeleton = () => (
  <div className="expiry-card-body">
    <div style={{ padding: "16px" }}>
      <div className="skeleton w-100" style={{ height: 108 }} />
      <div className="skeleton w-80 mt-2" />
    </div>
    <div className="row" style={{ padding: "0 12px" }}>
      {[0, 1].map((i) => (
        <div key={i} className="col-lg-6 col-md-12 col-xl-6">
          <div className="card notification-card relative standard">
            <div className="card-body topNotificationCard">
              <div className="notification-header-wrapper w-100">
                <div className="skeleton w-80" />
                <div className="skeleton w-100 mt-2" />
                <div className="skeleton w-50 mt-2" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ================================================================== */
/*  LicenseQuotaWidget (main)                                          */
/* ================================================================== */
const LicenseQuotaWidget = ({ widgetData, slug, title }) => {
  const history = useHistory();
  const { userRoleDetails } = useContext(AppContext);
  const { addToast } = useToasts();
  const [stats, setStats] = React.useState(null);
  const [isUpdateLoading, setIsUpdateLoading] = React.useState(false);

  const token = localStorage.getItem("access_token");
  const client = useClient();
  client.config.headers = { Authorization: `JWT ${token}` };

  const statusData = useQuery([apiRoutes.dashboardWidgetsData + "/" + slug], {
    client,
  });

  // Refresh interval
  React.useEffect(() => {
    const rate = widgetData?.widget?.config?.refreshRate;
    if (rate && rate > 0) {
      const id = setInterval(() => statusData.refetch(), rate * 1000);
      return () => clearInterval(id);
    }
  }, []);

  React.useEffect(() => {
    if (statusData?.data) setStats(statusData.data);
  }, [statusData]);

  const updateData = () => {
    const config = {
      headers: {
        Authorization: `JWT ${token}`,
        "content-type": "multipart/form-data",
      },
    };
    setIsUpdateLoading(true);
    Axios.get(apiRoutes.peopleStatsUpdate, config)
      .then((response) => {
        addToast(response?.data?.data?.message ?? "Widget updated Successfully!", {
          appearance: "success",
        });
        statusData.refetch();
      })
      .catch((error) => {
        addToast(error.detail ?? "Failed to update widget!", { appearance: "error" });
      })
      .finally(() => setIsUpdateLoading(false));
  };

  const handleRedirection = (key, status) => {
    const base = key === "employee" ? VW_ROUTES.EMPLOYEE_PROFILE : VW_ROUTES.VISITOR_PROFILES;
    history.push(status ? `${base}?status=${status}` : base);
  };

  // Derive display values
  const wd = stats?.widgetData;
  const license = wd?.license; // null when license manager disabled
  const hasLicense = license != null;

  // Global quota values (from license manager, fallback to local)
  // Note: JSON:API renderer camelCases nested keys (total_quota → totalQuota)
  const globalTotal = hasLicense ? license.totalQuota : wd?.quota || 0;
  const globalUsed = hasLicense ? license.totalUniqueProfiles : wd?.stats?.count || 0;
  const globalPercent = clampPct(
    hasLicense
      ? license.usagePercent
      : globalTotal > 0
      ? (globalUsed / globalTotal) * 100
      : 0
  );

  // This package
  const pkgCode = hasLicense ? license.packageCode : null;
  const pkgData = hasLicense
    ? (license.packages || []).find((p) => p.code === pkgCode)
    : null;
  // perPackageUsage keys may be camelCased by JSON:API renderer,
  // so try both the original pkgCode and a case-insensitive match
  const perPkg = license?.perPackageUsage || {};
  const pkgCountFromUsage = perPkg[pkgCode] ?? Object.entries(perPkg).find(
    ([k]) => k.toLowerCase().replace(/_/g, "") === (pkgCode || "").toLowerCase().replace(/_/g, "")
  )?.[1];
  const pkgCount = pkgData
    ? pkgData.count
    : hasLicense
    ? pkgCountFromUsage || 0
    : null;
  const pkgName = pkgData ? pkgData.name : pkgCode;
  const pkgPercent = globalTotal > 0 && pkgCount != null ? (pkgCount / globalTotal) * 100 : null;

  const state = getState(globalPercent);
  const categories = wd?.stats?.categories;

  return (
    <div className="card right-notification">
      <div className="card-header with-count">
        <span>{title}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {wd && (
            <span className={`lqw-badge badge-${state}`}>
              {Math.round(globalPercent)}%
            </span>
          )}
          {isWidgetRefreshable(userRoleDetails, widgetData) && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Update Data"
              onClick={() => !isUpdateLoading && updateData()}
              onKeyDown={handleKeyDown(() => !isUpdateLoading && updateData())}
              className={`refresh-button ${isUpdateLoading ? "spin" : ""}`}
              data-tip="Update Data"
            >
              <i className="iconsminds-refresh" />
            </span>
          )}
        </div>
      </div>

      {!wd ? (
        <Skeleton />
      ) : (
        <div className="expiry-card-body license-quota-widget">
          {/* Gauge — only when license manager data available */}
          {hasLicense && (
            <Gauge used={globalUsed} total={globalTotal} percent={globalPercent} />
          )}

          {/* Linear usage bar */}
          <UsageBar
            used={globalUsed}
            total={globalTotal}
            percent={globalPercent}
            pkgPercent={hasLicense ? pkgPercent : null}
          />

          {/* This package row — only when license data available */}
          {hasLicense && pkgName && <PackageRow name={pkgName} count={pkgCount} />}

          <ReactTooltip />

          {/* Category cards */}
          <div className="categories">
            {categories ? (
              Object.keys(categories).map((key) => (
                <CategoryCard
                  key={key}
                  categoryKey={key}
                  data={categories[key]}
                  onNavigate={handleRedirection}
                />
              ))
            ) : (
              /* Fallback: use old-style progress bar if no categories */
              <div style={{ width: "100%", padding: "0 4px" }}>
                <ProgressBar
                  percentage={globalTotal > 0 ? (globalUsed / globalTotal) * 100 : 0}
                />
                <div className="pt-1 justify-space-between">
                  <span>Profile usage :</span>
                  <span>
                    <b>{globalUsed}</b> / <b>{globalTotal}</b>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseQuotaWidget;
